/**
 * Blocked Users Page - Trade Loop v2.0
 *
 * Dedicated page for managing blocked users
 * Accessible from Settings page
 *
 * Features:
 * - View all blocked users
 * - Unblock functionality
 * - Empty state for no blocked users
 */

import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useToast } from "@/contexts/ToastContext";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "@/components/ui/avatar";
import {
  Ban,
  ShieldOff,
  ArrowLeft,
} from "lucide-react";
import { useUnblockUser } from "@/api/extensions/useBlockUser";

interface BlockedUser {
  id: string;
  connectionId?: string;
  firstName: string;
  lastName: string;
  company?: string;
  primaryTrade?: string;
  profileImageUrl?: string;
  status?: string;
}

export default function BlockedUsersPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const unblockUser = useUnblockUser();

  // Fetch connections to get blocked users
  // Always refetch when navigating to this page
  const { data: connections, isLoading: connectionsLoading } = useQuery({
    queryKey: ["/api/workflows/connections"],
    queryFn: async () => {
      const response = await api.get("/api/workflows/connections");
      console.log('[BlockedUsersPage] Raw API response:', response);
      // API returns array directly, not wrapped in { data: [] }
      return response;
    },
    refetchOnMount: 'always', // Always refetch fresh data when mounting
    staleTime: 0, // Consider data immediately stale
  });

  // Filter blocked users
  const blockedUsers = connections?.filter((conn: BlockedUser) => conn.status === 'blocked') || [];

  // Debug logging
  console.log('[BlockedUsersPage] Render - Connections data:', connections);
  console.log('[BlockedUsersPage] Render - Connection statuses:', connections?.map((c: any) => ({ id: c.id, firstName: c.firstName, status: c.status })));
  console.log('[BlockedUsersPage] Render - Filtered blocked users:', blockedUsers);
  console.log('[BlockedUsersPage] Render - Number of blocked users:', blockedUsers.length);

  // Handle unblock user
  const handleUnblockUser = async (userId: string, userName: string) => {
    console.log('[BlockedUsersPage] handleUnblockUser called with:', { userId, userName });

    try {
      // Perform the unblock mutation
      console.log('[BlockedUsersPage] Calling unblockUser.mutateAsync...');
      await unblockUser.mutateAsync({ user_id: userId });
      console.log('[BlockedUsersPage] unblockUser.mutateAsync completed successfully');

      // Explicitly refetch to ensure immediate UI update
      console.log('[BlockedUsersPage] Calling queryClient.refetchQueries...');
      await queryClient.refetchQueries({ queryKey: ["/api/workflows/connections"] });
      console.log('[BlockedUsersPage] queryClient.refetchQueries completed');

      // Check what data is in cache after refetch
      const updatedData = queryClient.getQueryData(["/api/workflows/connections"]);
      console.log('[BlockedUsersPage] Data after refetch:', updatedData);

      const updatedBlockedUsers = (updatedData as any)?.filter((conn: BlockedUser) => conn.status === 'blocked') || [];
      console.log('[BlockedUsersPage] Filtered blocked users after refetch:', updatedBlockedUsers);
      console.log('[BlockedUsersPage] Number of blocked users after refetch:', updatedBlockedUsers.length);

      showSuccess("User Unblocked", `${userName} has been unblocked`);
    } catch (error: any) {
      console.error('[BlockedUsersPage] Unblock error:', error);
      showError(
        "Unblock Failed",
        error.message || "Failed to unblock user. Please try again."
      );
    }
  };

  // Loading skeleton
  const renderLoadingSkeleton = () => (
    <Card className="bg-red-50 border-red-200">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <Skeleton className="w-12 h-12 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
          <Skeleton className="h-9 w-24" />
        </div>
      </CardContent>
    </Card>
  );

  return (
    <AppLayout activeTab="profile" title="Blocked Users">
      <div className="max-w-md md:max-w-2xl lg:max-w-4xl mx-auto px-4 pb-10 md:pb-0">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/settings")}
          className="mb-4 -ml-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Settings
        </Button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Blocked Users
          </h1>
          <p className="text-sm text-gray-600">
            Users you have blocked will not be able to see you in partner suggestions or send you connection requests.
          </p>
        </div>

        {/* Blocked Users List */}
        <div className="space-y-4">
          {connectionsLoading ? (
            // Loading state
            <>
              {[1, 2, 3].map((i) => (
                <div key={i}>{renderLoadingSkeleton()}</div>
              ))}
            </>
          ) : blockedUsers.length > 0 ? (
            // Blocked users exist
            blockedUsers.map((blockedUser: BlockedUser) => (
              <Card key={blockedUser.connectionId || blockedUser.id} className="bg-red-50 border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <Avatar className="w-12 h-12 border-2 border-red-300">
                        <AvatarImage
                          src={blockedUser.profileImageUrl || undefined}
                          alt={`${blockedUser.firstName} ${blockedUser.lastName}`}
                        />
                        <AvatarFallback className="bg-red-200 text-red-700 font-bold">
                          {blockedUser.firstName?.[0]}{blockedUser.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {blockedUser.firstName} {blockedUser.lastName}
                        </h3>
                        {blockedUser.company && (
                          <p className="text-sm text-gray-600">{blockedUser.company}</p>
                        )}
                        {blockedUser.primaryTrade && (
                          <p className="text-xs text-gray-500">{blockedUser.primaryTrade}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUnblockUser(blockedUser.id, `${blockedUser.firstName} ${blockedUser.lastName}`)}
                      disabled={unblockUser.isPending}
                      className="bg-white hover:bg-gray-50 text-red-600 border-red-300"
                    >
                      <ShieldOff className="w-4 h-4 mr-2" />
                      {unblockUser.isPending ? "Unblocking..." : "Unblock"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            // No blocked users
            <Card className="border-gray-200">
              <CardContent className="p-12">
                <div className="text-center text-gray-500">
                  <Ban className="mx-auto h-16 w-16 text-gray-300 mb-6" />
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    No Blocked Users
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    You haven't blocked any users yet. When you block someone, they will appear here and won't be able to see you in their partner suggestions.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
